/**
 * The PDApp's APP TAB in the share viewer (#757) — the share-view twin of
 * MiniAppEditor's 'run' face / MiniAppRunView: the app itself, running
 * full-bleed in its sandboxed preview iframe, with NO HTML-editor chrome (no
 * Preview|Source toggle, no design / export / refresh toolbar).
 *
 * Why not MiniAppRunView itself: it resolves the app and mints its preview
 * session through Drive-authenticated routes (`filesApi.getFileInfo`,
 * `/api/files` listing, `POST /api/preview/session`, the `/api/pdapp`
 * install gate) — none of which a link recipient can call (they 403 for a
 * cross-tenant recipient and a share session isn't accepted there). So this
 * renders the SAME runtime MiniAppRunView renders — `HtmlViewer`, which owns
 * the iframe, the SSE reload, the SDK bridges and the Papan accounts bar — but
 * fed from the share session the chrome already holds:
 *
 *   • baseUrl  = `<preview origin>/s/<sid>` — the entry's share SUB-SESSION
 *                (kind='share' is a valid preview-session token), scoped by the
 *                server to the `.pdapp` folder, so the preview server runs it
 *                Mode B under the app principal with per-email consent;
 *   • entry    = the server-computed `share.entryPath` (else the file name);
 *   • identity = the share id / acting tenant / verified email decoded from the
 *                token, exactly as HtmlEditor's share branch passes them, so the
 *                accounts bar + consent relay resolve for the recipient.
 *
 * No new authority: this is the path the share viewer already used to run the
 * app's index.html; only the editor chrome around the iframe is gone.
 * Differences from the in-app run tab: no install gate (a link recipient can't
 * install — the owner's installed state + the server's consent gate govern),
 * no "tasks" view for a triggers-only app (Run is disabled there instead), and
 * no debug-log capture (share previews never capture — HtmlViewer contract).
 */
import { useMemo } from 'react'
import { HtmlViewer, shareSessionContext, type OpenFileTargetWire, type OpenPdappTargetWire, type ShareSessionContext } from '@papan/collab/ui'
import { getPreviewOrigin, useServeTokenUrl } from '@papan/collab/client'

export interface SharePdappRunViewProps {
  /** The ENTRY's share sub-session token (the app's serve session). */
  sessionId: string
  /** The session's share context from its /info (a v2 token seals it, #791); absent → the
   *  v1 token's readable payload. */
  shareContext?: ShareSessionContext | null
  /** The entry file (index.html). */
  file: { id: string; name: string }
  /** Server-computed entry path relative to the serve root, when set. */
  entryPath?: string | null
  /** The app's run folder (the session's scoped root — the `.pdapp`). */
  appRunFolderId: string | null
  onOpenFileTarget?: (target: OpenFileTargetWire) => void
  /** `papan.openPdappByFolderId()` from the running app (#788). Without it the
   *  viewer drops the call — the app tab is where a shared PDApp runs. */
  onOpenPdappTarget?: (target: OpenPdappTargetWire) => void
}

export function SharePdappRunView({ sessionId, shareContext, file, entryPath, appRunFolderId, onOpenFileTarget, onOpenPdappTarget }: SharePdappRunViewProps) {
  // Display-only decode (the server re-verifies the token on every call) — the
  // same fields HtmlEditor's share branch hands HtmlViewer.
  const ctx = useMemo(() => {
    // The ONE share-session decode (the stored acting pair `at` / `ae` — the consent key).
    const c = shareContext ?? shareSessionContext(sessionId)
    return {
      shareId: c?.shareId ?? undefined,
      shareToken: c?.shareToken ?? undefined,
      actingTenantId: c?.actingTenantId ?? undefined,
      actingEmail: c?.actingEmail ?? undefined,
      verifiedEmail: c ? c.verifiedEmail : undefined,
    }
  }, [sessionId, shareContext])
  const origin = getPreviewOrigin()
  // The app's iframe loads on the session's SERVE token, never the session (the app can read
  // its own URL — see useServeTokenUrl); the parent keeps the session for its relays below.
  const sessionBaseUrl = origin ? `${origin}/s/${sessionId}` : null
  const baseUrl = useServeTokenUrl(sessionBaseUrl)
  if (sessionBaseUrl && !baseUrl) return <div className="relative min-h-0 min-w-0 flex-1" data-testid="share-pdapp-run" data-session={sessionId} />

  return (
    <div className="relative min-h-0 min-w-0 flex-1" data-testid="share-pdapp-run" data-session={sessionId}>
      <div className="absolute inset-0 flex flex-col">
        <HtmlViewer
          // A new session (identity re-bind, re-run) is a new app instance — the
          // same reset HtmlEditor's `key={sessionId}` gives in the share view.
          key={sessionId}
          content=""
          baseUrl={baseUrl}
          entryFile={entryPath ?? file.name}
          previewFileId={file.id}
          parentId={appRunFolderId}
          debugLogTarget={null}
          onOpenFileTarget={onOpenFileTarget ? (target) => onOpenFileTarget(target) : undefined}
          onOpenPdappTarget={onOpenPdappTarget ? (target) => onOpenPdappTarget(target) : undefined}
          accountsContext="share"
          shareId={ctx.shareId}
          shareActingTenantId={ctx.actingTenantId}
          shareActingEmail={ctx.actingEmail}
          shareToken={ctx.shareToken}
          shareSessionToken={sessionId}
          shareVerifiedEmail={ctx.verifiedEmail}
        />
      </div>
    </div>
  )
}
